import React, { useState, Fragment } from 'react';
import { EditIcon, ArchiveIcon, RefreshCwIcon, EyeIcon, MoreHorizontalIcon } from 'lucide-react';
import { Budget } from './BudgetManagement';
import { BudgetCard } from './BudgetCard';
import { BudgetStatusBadge } from './BudgetStatusBadge';
import { formatCurrency } from '../../utils/currencyFormatter';
interface BudgetListProps {
  budgets: Budget[];
  onEdit: (budget: Budget) => void;
  onArchive: (budgetId: string) => void;
  onRestore: (budgetId: string) => void;
  locale: string;
}
export function BudgetList({
  budgets,
  onEdit,
  onArchive,
  onRestore,
  locale
}: BudgetListProps) {
  const [expandedBudgetId, setExpandedBudgetId] = useState<string | null>(null);
  const toggleExpandBudget = (budgetId: string) => {
    setExpandedBudgetId(expandedBudgetId === budgetId ? null : budgetId);
  };
  return <div data-id="element-6734">
      {budgets.length === 0 ? <div className="text-center py-12" data-id="element-6735">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-6736">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-6737" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-6738">
            No budgets found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6739">
            Get started by creating a new budget.
          </p>
        </div> : <div className="overflow-hidden" data-id="element-6740">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-6741">
            <thead className="bg-gray-50" data-id="element-6742">
              <tr data-id="element-6743">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6744">
                  Budget Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6745">
                  Budget Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6746">
                  GL Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6747">
                  Entity / Site
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6748">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6749">
                  Period
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6750">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6751">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-6752">
              {budgets.map(budget => <Fragment key={budget.id} data-id="element-6753">
                  <tr className="hover:bg-gray-50" data-id="element-6754">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-6755">
                      {budget.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6756">
                      {budget.budgetTypeName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono" data-id="element-6757">
                      {budget.glCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6758">
                      {budget.entityName}
                      {budget.siteName && <>
                          <br data-id="element-6759" />
                          <span className="text-xs text-gray-400" data-id="element-6760">
                            {budget.siteName}
                          </span>
                        </>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6761">
                      <div className="font-medium" data-id="element-6762">
                        {formatCurrency(budget.remainingAmount, budget.currency, locale)}{' '}
                        <span className="text-xs text-gray-400" data-id="element-6763">remaining</span>
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-6764">
                        of{' '}
                        {formatCurrency(budget.totalAmount, budget.currency, locale)}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1" data-id="element-6765">
                        <div className={`h-1.5 rounded-full ${budget.remainingAmount / budget.totalAmount > 0.5 ? 'bg-green-500' : budget.remainingAmount / budget.totalAmount > 0.2 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                    width: `${Math.max(budget.remainingAmount / budget.totalAmount * 100, 0)}%`
                  }} data-id="element-6766"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6767">
                      {new Date(budget.startDate).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}{' '}
                      →{' '}
                      {new Date(budget.endDate).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6768">
                      <BudgetStatusBadge status={budget.status} data-id="element-6769" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6770">
                      <div className="flex justify-end space-x-2" data-id="element-6771">
                        <button onClick={() => toggleExpandBudget(budget.id)} className="text-blue-600 hover:text-blue-900" title="View Details" data-id="element-6772">
                          <EyeIcon className="h-4 w-4" data-id="element-6773" />
                        </button>
                        <button onClick={() => onEdit(budget)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-6774">
                          <EditIcon className="h-4 w-4" data-id="element-6775" />
                        </button>
                        {budget.status === 'Active' ? <button onClick={() => onArchive(budget.id)} className="text-gray-600 hover:text-gray-900" title="Archive" data-id="element-6776">
                            <ArchiveIcon className="h-4 w-4" data-id="element-6777" />
                          </button> : <button onClick={() => onRestore(budget.id)} className="text-green-600 hover:text-green-900" title="Restore" data-id="element-6778">
                            <RefreshCwIcon className="h-4 w-4" data-id="element-6779" />
                          </button>}
                      </div>
                    </td>
                  </tr>
                  {expandedBudgetId === budget.id && <tr data-id="element-6780">
                      <td colSpan={8} className="px-6 py-4 bg-gray-50" data-id="element-6781">
                        <BudgetCard budget={budget} locale={locale} data-id="element-6782" />
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>}
    </div>;
}